import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { X, Search, Download, Image, Video, File, FolderOpen, RefreshCw, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { logger } from "@/services/debug-logger";

interface ProjectFile {
  id: string;
  name: string;
  path: string;
  size: number;
  contentType: string;
  createdAt: string;
  pulseId?: number;
  pulseTitle?: string;
  sharedLink?: string;
  thumbnail?: string;
}

interface FileBrowserProps {
  projectId: string;
  onClose: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (contentType: string) => {
  if (contentType.startsWith('image/')) return Image;
  if (contentType.startsWith('video/')) return Video;
  return File;
};

const isImageOrVideo = (contentType: string): boolean => {
  return contentType.startsWith('image/') || contentType.startsWith('video/');
};

/**
 * Thumbnail that loads lazily: the file list arrives without thumbnails
 * (fast), and each image row fetches its own thumbnail only once it
 * scrolls into view. Non-images just show the type icon.
 */
function LazyThumb({ projectId, file }: { projectId: string; file: ProjectFile }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(file.thumbnail || null);
  const [failed, setFailed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const requestedRef = useRef(false);
  const isImage = file.contentType.startsWith('image/');
  const FileIcon = getFileIcon(file.contentType);

  useEffect(() => {
    if (!isImage || thumbUrl || failed || !ref.current) return;
    const el = ref.current;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting) || requestedRef.current) return;
      requestedRef.current = true;
      observer.disconnect();
      apiRequest('GET', `/api/projects/${projectId}/files/thumbnail?path=${encodeURIComponent(file.path)}`)
        .then((res) => res.json())
        .then((data) => {
          if (data?.thumbnail) setThumbUrl(data.thumbnail);
          else setFailed(true);
        })
        .catch(() => setFailed(true));
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isImage, thumbUrl, failed, projectId, file.path]);

  return (
    <div ref={ref} className="w-16 h-16 flex-shrink-0 flex items-center justify-center bg-gray-800 rounded border">
      {thumbUrl && !failed ? (
        <img
          src={thumbUrl}
          alt={file.name}
          className="w-full h-full object-cover rounded"
          onError={() => setFailed(true)}
        />
      ) : (
        <FileIcon className="h-8 w-8 text-gray-400" />
      )}
    </div>
  );
}

export function FileBrowser({ projectId, onClose }: FileBrowserProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const queryClient = useQueryClient();

  // Fetch project files
  const { data: files = [], isLoading, error } = useQuery<ProjectFile[]>({
    queryKey: [`/api/projects/${projectId}/files`],
  });

  // Refresh files list
  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: [`/api/projects/${projectId}/files`],
    });
  };

  // Automatically refresh files when component mounts (when file browser is opened)
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [`/api/projects/${projectId}/files`],
    });
  }, [projectId, queryClient]); // Refresh when component mounts or projectId changes

  // Filter files based on search query
  const filteredFiles = files.filter(file => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      file.name.toLowerCase().includes(query) ||
      (file.pulseTitle && file.pulseTitle.toLowerCase().includes(query))
    );
  });

  // Sort files chronologically (newest first)
  const sortedFiles = [...filteredFiles].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const handleDownload = async (file: ProjectFile) => {
    try {
      // Make authenticated request to download the file
      const response = await apiRequest('GET', `/api/attachments/download?path=${encodeURIComponent(file.path)}`);
      
      // Convert response to blob
      const blob = await response.blob();
      
      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      // TODO: Show user-friendly error message
    }
  };

  const handlePulseClick = (pulseId: number) => {
    // TODO: Navigate to pulse or open pulse details
    logger.debug('FILE_BROWSER', 'Navigate to pulse', { pulseId });
  };

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Project Files - {projectId}</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              className="text-gray-400 hover:text-white"
              title="Refresh files"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search files by name or pulse text..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* File List */}
        <ScrollArea className="flex-1 p-4">
          {isLoading && (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">Loading files...</div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                {(error as any)?.status === 403 || (error as any)?.message?.includes('403') ? (
                  <>
                    <Lock className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                    <div className="text-gray-400">Restricted Access</div>
                    <div className="text-gray-500 text-sm mt-1">Only project leads and assigned team members can view files</div>
                  </>
                ) : (
                  <div className="text-red-500">Error loading files</div>
                )}
              </div>
            </div>
          )}

          {!isLoading && !error && sortedFiles.length === 0 && (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">
                {searchQuery ? 'No files found matching your search' : 'No files found for this project'}
              </div>
            </div>
          )}

          {!isLoading && !error && sortedFiles.length > 0 && (
            <div className="space-y-2">
              {sortedFiles.map((file) => {
                const FileIcon = getFileIcon(file.contentType);
                return (
                  <div
                    key={file.id}
                    className="flex items-center gap-4 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    {/* Thumbnail/Icon — loads lazily when scrolled into view */}
                    <LazyThumb projectId={projectId} file={file} />

                    {/* File Name */}
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => handleDownload(file)}
                        className="text-left hover:text-blue-400 transition-colors truncate block w-full"
                        title="Click to download"
                      >
                        <span className="text-sm font-medium">{file.name}</span>
                      </button>
                    </div>

                    {/* File Size */}
                    <div className="w-20 text-sm text-gray-400 text-right">
                      {formatFileSize(file.size)}
                    </div>

                    {/* Pulse Link */}
                    <div className="w-48 min-w-0">
                      {file.pulseId && file.pulseTitle ? (
                        <button
                          onClick={() => handlePulseClick(file.pulseId!)}
                          className="text-xs text-blue-400 hover:text-blue-300 transition-colors truncate block w-full text-left"
                          title={file.pulseTitle}
                        >
                          {file.pulseTitle}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500">No pulse</span>
                      )}
                    </div>

                    {/* Creation Date */}
                    <div className="w-32 text-sm text-gray-400 text-right">
                      {format(new Date(file.createdAt), 'MMM dd, yyyy')}
                    </div>

                    {/* Download Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(file)}
                      className="text-gray-400 hover:text-white"
                      title="Download file"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t text-sm text-gray-500">
          {!isLoading && !error && sortedFiles.length > 0 && (
            <span>Showing {sortedFiles.length} file{sortedFiles.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}